import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createClient, type Session, type User } from "@supabase/supabase-js";
import { supabase, supabaseAnonKey, supabaseUrl } from "../lib/supabase";
import type { AdminUser } from "../types";

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  isAdmin: boolean;
  adminRole: AdminUser["role"] | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  refreshAdmin: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminRole, setAdminRole] = useState<AdminUser["role"] | null>(null);
  const [loading, setLoading] = useState(true);
  const verifiedUserIdRef = useRef<string | null>(null);

  const checkAdmin = async (nextSession: Session | null) => {
    if (!nextSession?.user.email) {
      verifiedUserIdRef.current = null;
      setIsAdmin(false);
      setAdminRole(null);
      return;
    }
    const { data, error } = await supabase
      .from("admin_allowlist")
      .select("email, role")
      .eq("email", nextSession.user.email.toLowerCase())
      .eq("active", true)
      .maybeSingle();
    // A short network interruption must not log an already verified admin out.
    // Only a successful allowlist response is authoritative for access changes.
    if (error) return;
    verifiedUserIdRef.current = data ? nextSession.user.id : null;
    setIsAdmin(Boolean(data));
    setAdminRole(data ? data.role as AdminUser["role"] : null);
  };

  const refreshAdmin = async () => {
    await checkAdmin(session);
  };

  useEffect(() => {
    let mounted = true;
    void supabase.auth.getSession().then(async ({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      await checkAdmin(data.session);
      if (mounted) setLoading(false);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (!mounted) return;
      setSession(nextSession);
      if (event === "USER_UPDATED" || event === "TOKEN_REFRESHED") return;
      // Supabase can emit SIGNED_IN again when an existing browser tab regains
      // focus. Rechecking access must not replace the mounted admin page and
      // thereby reset its currently open section or local form state.
      if (event === "SIGNED_IN" && nextSession?.user.id === verifiedUserIdRef.current) {
        window.setTimeout(() => { void checkAdmin(nextSession); }, 0);
        return;
      }
      setLoading(true);
      window.setTimeout(() => {
        void checkAdmin(nextSession).finally(() => mounted && setLoading(false));
      }, 0);
    });

    return () => {
      mounted = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!session) return;
    const recheckAccess = () => { void checkAdmin(session); };
    const recheckWhenVisible = () => { if (document.visibilityState === "visible") recheckAccess(); };
    const interval = window.setInterval(recheckAccess, 30_000);
    window.addEventListener("focus", recheckAccess);
    document.addEventListener("visibilitychange", recheckWhenVisible);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", recheckAccess);
      document.removeEventListener("visibilitychange", recheckWhenVisible);
    };
  }, [session]);

  const login = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  const logout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const updatePassword = async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    if (error) throw error;
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    const email = session?.user.email;
    if (!email) throw new Error("Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.");

    const verificationClient = createClient(supabaseUrl, supabaseAnonKey, { auth: { persistSession: false, autoRefreshToken: false } });
    const { error: verificationError } = await verificationClient.auth.signInWithPassword({ email, password: currentPassword });
    if (verificationError) {
      if (verificationError.message.toLowerCase().includes("invalid login credentials")) throw new Error("Das aktuelle Passwort ist nicht korrekt.");
      throw verificationError;
    }
    const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
    if (updateError) throw updateError;
  };

  const value = useMemo(
    () => ({
      session,
      user: session?.user || null,
      isAdmin,
      adminRole,
      loading,
      login,
      logout,
      updatePassword,
      changePassword,
      refreshAdmin,
    }),
    [session, isAdmin, adminRole, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used within AuthProvider");
  return value;
}
